import { ConnectionState, LocalAudioTrack, LocalVideoTrack, RoomEvent, Track } from "livekit-client";

const kRoomPlaceholderStateKey = "__librettoRoomPlaceholderTracks";

const sleepMs = (ms) =>
  new Promise((resolve) => {
    try {
      window.setTimeout(resolve, ms);
    } catch {
      resolve();
    }
  });

const getPlaceholderState = (room) => {
  if (!room) return null;
  if (!room[kRoomPlaceholderStateKey]) {
    room[kRoomPlaceholderStateKey] = {
      cleanupRegistered: false,
      ensureInFlight: null,
      dedupeRegistered: false,
      dedupeHandler: null,
      placeholderCanvas: null,
      placeholderCanvasIntervalId: null,
      placeholderAudioContext: null,
      placeholderOscillator: null,
      placeholderGainNode: null,
    };
  }
  return room[kRoomPlaceholderStateKey];
};

export const releasePlaceholderResources = (room, source) => {
  const state = getPlaceholderState(room);
  if (!state) return;

  if (source === Track.Source.Camera) {
    if (state.placeholderCanvasIntervalId != null) {
      try {
        window.clearInterval(state.placeholderCanvasIntervalId);
      } catch {}
      state.placeholderCanvasIntervalId = null;
    }
    state.placeholderCanvas = null;
  }

  if (source === Track.Source.Microphone) {
    try {
      state.placeholderOscillator?.stop?.();
    } catch {}
    state.placeholderOscillator = null;

    try {
      state.placeholderAudioContext?.close?.();
    } catch {}
    state.placeholderAudioContext = null;
    state.placeholderGainNode = null;
  }
};

const cleanupPlaceholderState = (room) => {
  const state = getPlaceholderState(room);
  if (!state) return;

  if (state.dedupeHandler) {
    try {
      room.off?.(RoomEvent.LocalTrackPublished, state.dedupeHandler);
    } catch {}
    state.dedupeHandler = null;
    state.dedupeRegistered = false;
  }

  releasePlaceholderResources(room, Track.Source.Camera);
  releasePlaceholderResources(room, Track.Source.Microphone);

  state.ensureInFlight = null;
};

const createSilentAudioMediaTrack = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    throw new Error("AudioContext not supported");
  }
  const audioContext = new AudioCtx();
  const destination = audioContext.createMediaStreamDestination();

  // Keep the node graph alive with an oscillator at 0 gain.
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  gainNode.gain.value = 0;
  oscillator.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start();

  // Best-effort: attempt to resume (often allowed since joining a room is user-initiated).
  void audioContext.resume?.().catch(() => {});

  const track = destination.stream.getAudioTracks()[0] || null;
  if (!track) {
    throw new Error("Failed to create silent audio track");
  }
  return { track, audioContext, oscillator, gainNode };
};

const createBlackVideoMediaTrack = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 360;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  const stream = canvas.captureStream?.(1);
  if (!stream) {
    throw new Error("Canvas captureStream not supported");
  }

  const track = stream.getVideoTracks()[0] || null;
  if (!track) {
    throw new Error("Failed to create black video track");
  }

  // Ensure the canvas keeps producing frames (low frequency to minimize overhead).
  const intervalId = window.setInterval(() => {
    try {
      const ctx2 = canvas.getContext("2d");
      if (!ctx2) return;
      ctx2.fillStyle = "#000";
      ctx2.fillRect(0, 0, canvas.width, canvas.height);
    } catch {}
  }, 1000);

  return { track, canvas, intervalId };
};

const waitForLocalPublication = async (
  localParticipant,
  source,
  { shouldWaitForRealTrack = false, timeoutMs = 1500, stepMs = 150 } = {},
) => {
  try {
    const first = localParticipant.getTrackPublication?.(source);
    if (first) return first;
  } catch {}

  if (!shouldWaitForRealTrack) return null;

  const steps = Math.max(1, Math.floor(timeoutMs / stepMs));
  for (let i = 0; i < steps; i++) {
    await sleepMs(stepMs);
    try {
      const pub = localParticipant.getTrackPublication?.(source);
      if (pub) return pub;
    } catch {}
  }
  return null;
};

export async function ensurePlaceholderMicCamTracksPublished(
  room,
  { expectMicrophoneEnabled = false, expectCameraEnabled = false } = {},
) {
  if (!room) return;
  if (typeof window === "undefined") return;
  if (room.state !== ConnectionState.Connected) return;

  const state = getPlaceholderState(room);
  if (!state) return;

  if (state.ensureInFlight) return state.ensureInFlight;

  if (!state.cleanupRegistered) {
    state.cleanupRegistered = true;
    room.on(RoomEvent.Disconnected, () => cleanupPlaceholderState(room));
  }

  const localParticipant = room.localParticipant;
  if (!localParticipant) return;

  if (!state.dedupeRegistered) {
    state.dedupeRegistered = true;
    state.dedupeHandler = async () => {
      try {
        const lp = room.localParticipant;
        if (!lp) return;
        const pubs = Array.from(lp.trackPublications?.values?.() ?? []);
        const dedupeSource = async (source) => {
          const sameSource = pubs.filter((p) => p?.source === source);
          if (sameSource.length <= 1) return;
          const hasNonUserProvided = sameSource.some((p) => p?.track && p.track.isUserProvided === false);
          if (!hasNonUserProvided) return;

          for (const p of sameSource) {
            if (p?.track && p.track.isUserProvided === true) {
              try {
                await lp.unpublishTrack(p.track);
                releasePlaceholderResources(room, source);
              } catch {}
            }
          }
        };
        await dedupeSource(Track.Source.Microphone);
        await dedupeSource(Track.Source.Camera);
      } catch {}
    };
    room.on(RoomEvent.LocalTrackPublished, state.dedupeHandler);
    // Also run once in case duplicate tracks already exist (race during join).
    try {
      void state.dedupeHandler();
    } catch {}
  }

  state.ensureInFlight = (async () => {
    // 1) Ensure microphone track exists (placeholder).
    try {
      let micPub = localParticipant.getTrackPublication?.(Track.Source.Microphone);
      if (!micPub) {
        const shouldWaitLong = !!expectMicrophoneEnabled || !!localParticipant.isMicrophoneEnabled;
        micPub = await waitForLocalPublication(localParticipant, Track.Source.Microphone, {
          shouldWaitForRealTrack: true,
          timeoutMs: shouldWaitLong ? 1500 : 200,
        });
      }

      if (!micPub) {
        // Re-check right before publish to avoid racing other publishers.
        micPub = localParticipant.getTrackPublication?.(Track.Source.Microphone);
      }

      if (!micPub) {
        const { track, audioContext, oscillator, gainNode } = createSilentAudioMediaTrack();
        const localAudioTrack = new LocalAudioTrack(track, {}, true, audioContext);
        localAudioTrack.source = Track.Source.Microphone;

        state.placeholderAudioContext = audioContext;
        state.placeholderOscillator = oscillator;
        state.placeholderGainNode = gainNode;

        await localParticipant.publishTrack(localAudioTrack, {
          source: Track.Source.Microphone,
          name: "Microphone",
          stream: "camera",
        });
        await localAudioTrack.mute();
      }
    } catch {}

    // 2) Ensure camera track exists (placeholder).
    try {
      let camPub = localParticipant.getTrackPublication?.(Track.Source.Camera);
      if (!camPub) {
        const shouldWaitLong = !!expectCameraEnabled || !!localParticipant.isCameraEnabled;
        camPub = await waitForLocalPublication(localParticipant, Track.Source.Camera, {
          shouldWaitForRealTrack: true,
          timeoutMs: shouldWaitLong ? 1500 : 200,
        });
      }

      if (!camPub) {
        // Re-check right before publish to avoid racing other publishers.
        camPub = localParticipant.getTrackPublication?.(Track.Source.Camera);
      }

      if (!camPub) {
        const { track, canvas, intervalId } = createBlackVideoMediaTrack();
        const localVideoTrack = new LocalVideoTrack(track, {}, true);
        localVideoTrack.source = Track.Source.Camera;

        state.placeholderCanvas = canvas;
        state.placeholderCanvasIntervalId = intervalId;

        await localParticipant.publishTrack(localVideoTrack, {
          source: Track.Source.Camera,
          name: "Camera",
          stream: "camera",
        });
        await localVideoTrack.mute();
      }
    } catch {}
  })()
    .catch(() => {})
    .finally(() => {
      state.ensureInFlight = null;
    });

  return state.ensureInFlight;
}
